<script setup lang="ts">
import MmDialog from '../base/MmDialog.vue'
import MmButton from '../base/MmButton.vue'
import CreatorBadge from './badges/CreatorBadge.vue'
import { useCreator } from './useCreator'
import { chartDescription } from './insights/format'
import { badgeMetadata } from './contracts.mjs'
const { t, te, locale } = useI18n()
const apiBase = useRuntimeConfig().public.apiBaseUrl
const { badges, identity, legacyRewardsAllowed, markSeen } = useCreator()
const open = ref(false), busy = ref(false), error = ref(false)
const current = ref<string[]>([])
const shown = useState<string[]>('mm-creator-celebrated', () => [])
const earned = computed(() => badges.value?.badges.filter(b => current.value.includes(b.badge_key)) || [])
const definition = (key: string) => badges.value?.definitions.find(d => d.key === key)
const metadata = (key: string) => badgeMetadata(definition(key) || { key, ...badges.value?.badges.find(item => item.badge_key === key) }, locale.value, t, te, apiBase)
const name = (key: string) => metadata(key).name
let dismissal = 0
watch([identity, legacyRewardsAllowed], () => { dismissal++; open.value = false; current.value = []; busy.value = false; error.value = false }, { flush: 'sync' })
function reveal() {
  if (!identity.value || !legacyRewardsAllowed.value) { open.value = false; current.value = []; return }
  if (open.value) return
  if (import.meta.client && document.querySelector('.mm-overlay-root [role="dialog"], .mm-overlay-root [role="menu"]')) return
  const fresh = badges.value?.badges.filter(b => b.new && b.tier > 0 && !shown.value.includes(`${identity.value}:${b.badge_key}:${b.tier}`)).slice(0, 100) || []
  if (!fresh.length) return
  current.value = fresh.map(b => b.badge_key)
  shown.value.push(...fresh.map(b => `${identity.value}:${b.badge_key}:${b.tier}`))
  open.value = true
}
watch([badges, identity], reveal, { immediate: true })
let observer: MutationObserver | undefined
onMounted(() => { observer = new MutationObserver(reveal); observer.observe(document.body, { childList: true, subtree: true }); reveal() })
onBeforeUnmount(() => observer?.disconnect())
async function dismiss() {
  if (busy.value) return
  const owner = identity.value, revision = ++dismissal
  const isCurrent = () => owner === identity.value && revision === dismissal
  busy.value = true; error.value = false
  try { await markSeen([...current.value]); if (isCurrent()) open.value = false }
  catch { if (isCurrent()) error.value = true }
  finally { if (isCurrent()) busy.value = false }
}
</script>
<template><MmDialog :model-value="open" :title="t('creator.newBadge')" display-title @update:model-value="!$event && dismiss()"><div class="badge-celebration"><p>{{ t('creator.badgeCongrats') }}</p><div v-for="badge in earned" :key="badge.badge_key" class="celebration-item"><CreatorBadge :src="metadata(badge.badge_key).iconUrl" retryable :icon="metadata(badge.badge_key).iconKey" :tier="badge.tier" :single="definition(badge.badge_key)?.thresholds.length === 1" :label="name(badge.badge_key)" /><strong>{{ name(badge.badge_key) }}</strong><p v-if="chartDescription(badge.badge_key, metadata(badge.badge_key).description, t)" class="badge-description">{{ chartDescription(badge.badge_key, metadata(badge.badge_key).description, t) }}</p></div><p v-if="error" class="error-text" role="alert">{{ t('creator.unavailable') }}</p></div><template #actions><MmButton :disabled="busy" @click="dismiss">{{ t(error ? 'common.retry' : 'creator.nice') }}</MmButton></template></MmDialog></template>
<style scoped>
.badge-celebration { display: flex; flex-wrap: wrap; justify-content: center; gap: var(--s-5); text-align: center; }
.badge-celebration > p { width: 100%; color: var(--text-2); }
.celebration-item { display: grid; justify-items: center; gap: var(--s-2); min-width: 0; max-width: 100%; overflow-wrap: anywhere; animation: badge-drop var(--d-story) var(--ease-spring) both; }
.celebration-item :deep(.creator-badge) { width: 96px; height: 96px; }
.celebration-item strong { font-size: var(--t-small); }
.badge-description { max-width: 100%; color: var(--text-2); font: 400 var(--t-small)/1.5 var(--font-ui); white-space: pre-wrap; }
@keyframes badge-drop { from { opacity: 0; transform: translateY(14px) rotate(-25deg) scale(.8); } }
@media(prefers-reduced-motion:reduce) { .celebration-item { animation: none; } }
</style>
